import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Input,
  Modal,
  Segmented,
  Select,
  Typography,
  message,
} from "antd";
import { useReviewCredits } from "../../hooks/useReviewCredits";
import { ScanProgressModal } from "../../components/ScanProgressModal";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  FormOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import type { Language, ReviewCategory } from "../../types";
import { useUiState } from "../../state";
import { textMap } from "../../i18n";
import {
  ApiError,
  createStudioSeriesApi,
  fetchStudioSeriesList,
  patchReviewTaskSeries,
  getScanProgressStepLabels,
  scanClaims,
} from "../../services/compliance";
import { appendStudioScan, newScanDisplayId } from "./studioScanHistoryStorage";
import { type StudioSeries, studioSeriesDtoToUi } from "./studioSeries";
import {
  DRAFT_KEY,
  MAX_CLAIM_CHARS,
  type DraftShape,
  type VerticalKey,
  verticalToCategory,
} from "./studioShared";
import "../../styles/studio-workbench.css";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

export function StudioNewScanPage() {
  const { lang, themeMode } = useUiState();
  const t = textMap[lang];
  const navigate = useNavigate();
  const {
    reviewCredits,
    billingReady,
    isUnlimited,
    applyFromScanResult,
  } = useReviewCredits();

  const [internalName, setInternalName] = useState("");
  const [verticalKey, setVerticalKey] = useState<VerticalKey>("cosmetics");
  const [scanLang, setScanLang] = useState<Language>(lang);
  const [claimsText, setClaimsText] = useState("");
  const [includeFda, setIncludeFda] = useState(true);
  const [attachSeries, setAttachSeries] = useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | undefined>(
    undefined,
  );
  const [seriesList, setSeriesList] = useState<StudioSeries[]>([]);
  const [createSeriesOpen, setCreateSeriesOpen] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanStageLine, setScanStageLine] = useState("");
  const [scanProgressPercent, setScanProgressPercent] = useState(0);
  const [scanActiveStepIndex, setScanActiveStepIndex] = useState(0);
  const [scanProgressComplete, setScanProgressComplete] = useState(false);

  const scanStepLabels = useMemo(
    () => getScanProgressStepLabels(scanLang),
    [scanLang],
  );

  useEffect(() => {
    setScanLang(lang);
  }, [lang]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw) as DraftShape;
      if (d && typeof d.claimsText === "string") {
        setInternalName(d.internalName ?? "");
        setVerticalKey(d.verticalKey ?? "cosmetics");
        setClaimsText(d.claimsText);
        setIncludeFda(d.includeFda ?? true);
        setAttachSeries(d.attachSeries ?? false);
        if (typeof d.selectedSeriesId === "string" && d.selectedSeriesId) {
          setSelectedSeriesId(d.selectedSeriesId);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchStudioSeriesList()
      .then((rows) => {
        if (!cancelled) {
          setSeriesList(rows.map(studioSeriesDtoToUi));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSeriesList([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveDraft = useCallback(() => {
    const draft: DraftShape = {
      internalName,
      verticalKey,
      claimsText,
      includeFda,
      attachSeries,
      selectedSeriesId: selectedSeriesId || undefined,
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    message.success(lang === "zh" ? "草稿已保存" : "Draft saved");
  }, [
    attachSeries,
    claimsText,
    includeFda,
    internalName,
    lang,
    selectedSeriesId,
    verticalKey,
  ]);

  const verticalOptions = useMemo(
    () => [
      { value: "cosmetics" as const, label: t.studioVerticalCosmetic },
      { value: "pet_food" as const, label: t.studioVerticalFood },
      { value: "baby" as const, label: t.studioVerticalBaby },
      { value: "supplements" as const, label: t.studioVerticalSupplement },
    ],
    [t],
  );

  const riskRows = useMemo(
    () => [
      { key: "d", label: t.studioRiskDisease, level: "high" as const },
      { key: "a", label: t.studioRiskAdditive, level: "high" as const },
      { key: "i", label: t.studioRiskIngredient, level: "high" as const },
      { key: "c", label: t.studioRiskComparative, level: "medium" as const },
      { key: "e", label: t.studioRiskEco, level: "medium" as const },
    ],
    [t],
  );

  const refreshSeriesList = async () => {
    try {
      const rows = await fetchStudioSeriesList();
      setSeriesList(rows.map(studioSeriesDtoToUi));
    } catch {
      message.error(t.studioSeriesLoadFailed);
    }
  };

  const submitNewSeries = async () => {
    if (!newSeriesName.trim()) {
      message.warning(t.studioSeriesNameRequired);
      throw new Error("validation");
    }
    try {
      const dto = await createStudioSeriesApi(newSeriesName);
      const ui = studioSeriesDtoToUi(dto);
      await refreshSeriesList();
      setSelectedSeriesId(ui.id);
      setNewSeriesName("");
      message.success(t.studioSeriesCreateSuccess);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : t.studioSeriesLoadFailed;
      message.error(msg);
      throw e;
    }
  };

  const runScan = async () => {
    const content = claimsText.trim();
    if (!content) {
      message.warning(t.studioNoClaims);
      return;
    }
    if (billingReady && !isUnlimited && reviewCredits <= 0) {
      Modal.confirm({
        title: t.outOfCredits,
        content: t.reviewUpsell,
        okText: t.upgradePlanNow,
        cancelText: t.later,
        onOk: () => navigate("/app/upgrade"),
      });
      return;
    }
    if (attachSeries) {
      if (!selectedSeriesId) {
        message.warning(t.studioSeriesNeedSelect);
        return;
      }
    }
    const category: ReviewCategory = verticalToCategory(verticalKey);
    setScanning(true);
    setScanProgressComplete(false);
    setScanActiveStepIndex(0);
    setScanProgressPercent(8);
    setScanStageLine(
      scanLang === "zh" ? "正在连接审查服务…" : "Connecting to compliance engine…",
    );
    try {
      const res = await scanClaims(content, scanLang, category, {
        productDisplayName: internalName.trim() || undefined,
        seriesId:
          attachSeries && selectedSeriesId ? selectedSeriesId : undefined,
        onProgress: (p) => {
          setScanStageLine(p.label);
          setScanProgressPercent(p.percent);
          setScanActiveStepIndex(
            p.isComplete ? scanStepLabels.length : p.index,
          );
          setScanProgressComplete(Boolean(p.isComplete));
        },
      });
      applyFromScanResult(res.credit_balance);
      const at = Date.now();
      const scanDisplayId = res.task_id ?? newScanDisplayId();
      appendStudioScan({
        scanDisplayId,
        completedAt: at,
        internalName,
        taskId: res.task_id ?? scanDisplayId,
        verticalKey,
        scanLang,
        claimsText: content,
        result: res,
        ...(attachSeries && selectedSeriesId
          ? { seriesId: selectedSeriesId }
          : {}),
      });
      if (
        res.task_id &&
        attachSeries &&
        selectedSeriesId
      ) {
        try {
          await patchReviewTaskSeries(res.task_id, selectedSeriesId);
        } catch {
          message.warning(t.studioSeriesSyncFailed);
        }
      }
      navigate(`/studio/scans/${encodeURIComponent(scanDisplayId)}`, {
        replace: true,
        state: { studioDetailBack: "new" as const },
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        Modal.confirm({
          title: t.outOfCredits,
          content: t.reviewUpsell,
          okText: t.upgradePlanNow,
          cancelText: t.later,
          onOk: () => navigate("/app/upgrade"),
        });
        return;
      }
      const detail = e instanceof ApiError ? e.message : t.studioScanFailed;
      message.error(detail);
    } finally {
      setScanning(false);
      setScanStageLine("");
      setScanProgressPercent(0);
      setScanActiveStepIndex(0);
      setScanProgressComplete(false);
    }
  };

  return (
    <div className="studio-wb-layout">
      <ScanProgressModal
        open={scanning}
        title={t.studioScanning}
        steps={scanStepLabels}
        activeStepIndex={scanActiveStepIndex}
        percent={scanProgressPercent}
        statusLine={
          scanStageLine ||
          (scanLang === "zh" ? "准备中…" : "Preparing…")
        }
        isComplete={scanProgressComplete}
        variant={themeMode === "dark" ? "dark" : "light"}
      />
      <div className="studio-wb-form-col">
        <div style={{ marginBottom: 28 }}>
          <Typography.Title level={2} style={{ marginBottom: 8, marginTop: 0 }}>
            {t.studioFormTitle}
          </Typography.Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t.studioFormSubtitle}
          </Paragraph>
        </div>

        <section className="studio-wb-section">
          <div className="studio-wb-section-head">
            <FormOutlined style={{ color: "var(--wb-primary)" }} />
            <h3 className="studio-wb-section-title">{t.studioMetaTitle}</h3>
          </div>
          <div className="studio-wb-grid-3">
            <div>
              <label className="studio-wb-label">{t.studioInternalName}</label>
              <Input
                value={internalName}
                onChange={(e) => setInternalName(e.target.value)}
                placeholder={t.studioInternalNamePh}
                maxLength={120}
                style={{ borderRadius: 14 }}
              />
            </div>
            <div>
              <label className="studio-wb-label">{t.studioVerticalLabel}</label>
              <Select
                className="studio-wb-select-full"
                style={{ width: "100%" }}
                value={verticalKey}
                options={verticalOptions}
                onChange={(v) => setVerticalKey(v as VerticalKey)}
              />
            </div>
            <div>
              <label className="studio-wb-label">
                {scanLang === "zh" ? "分析语言" : "Analysis language"}
              </label>
              <Segmented
                block
                value={scanLang}
                options={[
                  { label: "English", value: "en" },
                  { label: "中文", value: "zh" },
                ]}
                onChange={(v) => setScanLang(v as Language)}
              />
            </div>
          </div>
        </section>

        <section className="studio-wb-section">
          <div className="studio-wb-section-head">
            <FileTextOutlined style={{ color: "var(--wb-primary)" }} />
            <h3 className="studio-wb-section-title">{t.studioClaimsTitle}</h3>
          </div>
          <label className="studio-wb-label">{t.studioClaimsHint}</label>
          <div className="studio-wb-textarea-wrap">
            <TextArea
              value={claimsText}
              onChange={(e) => setClaimsText(e.target.value)}
              placeholder={t.studioClaimsPh}
              rows={10}
              maxLength={MAX_CLAIM_CHARS}
              style={{ borderRadius: 14, paddingBottom: 36 }}
            />
            <span className="studio-wb-char-count">
              {claimsText.length} / {MAX_CLAIM_CHARS}
            </span>
          </div>
        </section>

        <section className="studio-wb-section">
          <div className="studio-wb-section-head">
            <SettingOutlined style={{ color: "var(--wb-primary)", fontSize: 18 }} />
            <h3 className="studio-wb-section-title">{t.studioOptionsTitle}</h3>
          </div>
          <div className="studio-wb-options-grid">
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <label style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <Checkbox
                    checked={includeFda}
                    onChange={(e) => setIncludeFda(e.target.checked)}
                  />
                  <div>
                    <Text strong>{t.studioOptFda}</Text>
                    <Paragraph
                      type="secondary"
                      style={{ fontSize: 11, marginBottom: 0, marginTop: 4 }}
                    >
                      {t.studioOptFdaDesc}
                    </Paragraph>
                  </div>
                </div>
              </label>
              <label style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <Checkbox
                    checked={attachSeries}
                    onChange={(e) => setAttachSeries(e.target.checked)}
                  />
                  <div>
                    <Text strong>{t.studioOptSeries}</Text>
                    <Paragraph
                      type="secondary"
                      style={{ fontSize: 11, marginBottom: 0, marginTop: 4 }}
                    >
                      {t.studioOptSeriesDesc}
                    </Paragraph>
                  </div>
                </div>
              </label>
            </div>
            <div>
              <label className="studio-wb-label">{t.studioSeriesLabel}</label>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Select
                  disabled={!attachSeries}
                  allowClear
                  placeholder={t.studioSeriesPlaceholder}
                  style={{ flex: "1 1 160px", minWidth: 140 }}
                  value={selectedSeriesId}
                  options={seriesList.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  onChange={(v) => setSelectedSeriesId(v ?? undefined)}
                  onDropdownVisibleChange={(open) => {
                    if (open) refreshSeriesList();
                  }}
                />
                <Button
                  disabled={!attachSeries}
                  onClick={() => {
                    refreshSeriesList();
                    setNewSeriesName("");
                    setCreateSeriesOpen(true);
                  }}
                >
                  {t.studioSeriesNewBtn}
                </Button>
              </div>
            </div>
          </div>
        </section>

        <div className="studio-wb-actions">
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t.studioEstTime}
            </Text>
            {billingReady ? (
              <div className="sentinel-credit-pill studio-wb-credit-pill">
                {t.creditsLabel}: {reviewCredits.toLocaleString()}
              </div>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Button size="large" onClick={saveDraft}>
              {t.studioSaveDraft}
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<ThunderboltOutlined />}
              loading={scanning}
              onClick={() => void runScan()}
              style={{
                borderRadius: 14,
                fontWeight: 700,
                background: "var(--wb-primary)",
              }}
            >
              {scanning ? t.studioScanning : t.studioRunScan}
            </Button>
          </div>
        </div>
      </div>

      <Modal
        title={t.studioSeriesCreateModalTitle}
        open={createSeriesOpen}
        okText={lang === "zh" ? "创建" : "Create"}
        cancelText={lang === "zh" ? "取消" : "Cancel"}
        onCancel={() => {
          setCreateSeriesOpen(false);
          setNewSeriesName("");
        }}
        onOk={() => submitNewSeries()}
        destroyOnClose
      >
        <Input
          value={newSeriesName}
          onChange={(e) => setNewSeriesName(e.target.value)}
          placeholder={t.studioSeriesNamePh}
          maxLength={120}
        />
      </Modal>

      <aside className="studio-wb-aside">
        <div className="studio-wb-section">
          <Text strong style={{ display: "block", marginBottom: 16 }}>
            {t.studioRiskPanelTitle}
          </Text>
          <div className="studio-wb-risk-list">
            {riskRows.map((row) => (
              <div key={row.key} className="studio-wb-risk-row">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    className={`studio-wb-dot ${row.level === "high" ? "high" : "medium"}`}
                  />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{row.label}</span>
                </div>
                <CheckCircleOutlined style={{ color: "#004e32", opacity: 0.6 }} />
              </div>
            ))}
          </div>
          <div className="studio-wb-audit">
            <Text strong style={{ fontSize: 10, letterSpacing: "0.08em" }}>
              {t.studioAuditTitle}
            </Text>
            <Paragraph style={{ fontSize: 11, marginBottom: 0, marginTop: 8 }}>
              {t.studioAuditBody}
            </Paragraph>
          </div>
        </div>
        <div className="studio-wb-section">
          <Text strong style={{ fontSize: 12 }}>
            {t.studioNeedHelpTitle}
          </Text>
          <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 8 }}>
            {t.studioNeedHelpBody}
          </Paragraph>
          <Button type="link" size="small" style={{ padding: 0 }}>
            {t.studioDocLink}
          </Button>
        </div>
      </aside>
    </div>
  );
}
